import React, {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {Link, useNavigate} from "react-router-dom";
import {useFormik} from "formik";
import {Alert, CircularProgress} from "@mui/material";
import ReCAPTCHA from "react-google-recaptcha";
import Header from "../components/Header";
import TextBox from "../components/TextBox";
import {hostSignup} from "../store/thunks/hostActions";
import {hostSignupReset} from "../store/slices/hostSlices";
import {registrationSchema} from "../schemas";

const propertyManagementOptions = [
    {label: "Streamline", value: "streamline"},
    {label: "Guesty", value: "guesty"},
    {label: "Other", value: "other"},
];

const SignupScreen = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [signupError, setSignupError] = useState(null);
    const [captchaValue, setCaptchaValue] = useState(null);

    const onCaptchaChange = (token) => {
        setCaptchaValue(token); // Set the CAPTCHA response token
    };

    const initialValues = {
        firstName: "",
        lastName: "",
        email: "",
        phoneNumber: "",
        password: "",
        isManager: "",
        propertyManagementSoftware: "",
        selfOnboarding: "",
        address: {
            line1: "",
            line2: "",
            city: "",
            state: "",
            zipCode: "",
        },
    };

    const {values, errors, touched, handleBlur, handleChange, getIn} =
        useFormik({
            initialValues: initialValues,
            validationSchema: registrationSchema,
        });

    const {loading, success, error} = useSelector((state) => {
        return state.hostSignUp;
    });
    const {hostInfo} = useSelector((state) => {
        return state.host;
    });

    const [confirmPassword, setConfirmPassword] = useState("");

    const handleSignUp = () => {
        if (!captchaValue) {
            setSignupError("Please verify that you are not a robot.");
            return;
        }


        if (errors.address && Object.keys(errors.address).length > 0) {
            console.log("errors", errors.address);
            setSignupError(errors.address[Object.keys(errors.address)[0]]);

            return;
        }

        if (Object.keys(errors).length > 0) {
            console.log("errors", errors.address);
            setSignupError(errors[Object.keys(errors)[0]]);

            return;
        }

        if (Object.keys(errors).length === 0 && values.password === confirmPassword && captchaValue) {
            // Include the captchaValue when dispatching
            // console.log(captchaValue)
            dispatch(hostSignup({...values, captchaResponse: captchaValue}));
            // console.log(captchaValue)
        }

    };

    useEffect(() => {
        if (hostInfo) {
            navigate("/host");
        }
        if (success) {
            navigate("/please-verify");
            dispatch(hostSignupReset());
        }
        if (error) {
            setTimeout(() => {
                dispatch(hostSignupReset());
            }, 2000);
        }
    }, [success, navigate, hostInfo, error]);

    useEffect(() => {
        setTimeout(() => {
            signupError && setSignupError(null);
        }, 3000);
    }, [signupError]);

    return (
        <>
            <div className="flex flex-col min-h-screen w-full h-fit">
                <Header/>
                {error && (
                    <Alert
                        severity="error"
                        style={{
                            position: "sticky",
                            width: "90%",
                            margin: "auto",
                            top: 10,
                            left: 0,
                            right: 0,
                        }}
                    >
                        <strong>{error}</strong>
                    </Alert>
                )}
                {signupError && (
                    <Alert
                        severity="error"
                        style={{
                            position: "sticky",
                            width: "90%",
                            margin: "auto",
                            top: 10,
                            left: 0,
                            right: 0,
                        }}
                    >
                        {signupError}
                    </Alert>
                )}

                <div className="flex justify-center bg-white h-full w-full flex-grow">
                    <div className="laptop:w-[450px] tabletOnly:w-[400px] mobile:w-[320px] text-center pb-10">
                        {/* TOP */}
                        <div className="top mt-5">
                            <h2 className="font-bold text-lg">Sign Up</h2>
                            <p className="text-base2 text-light">
                                Already have an account?{" "}
                                <label className=" text-primary">
                                    <Link to={"/login"}>Sign In</Link>
                                </label>
                            </p>
                        </div>

                        {/* FORM */}

                        <div className="form mt-[50px]">
                            <div className="flex items-center gap-x-2">
                                <TextBox
                                    label={"First Name"}
                                    name={"firstName"}
                                    value={values.firstName}
                                    onChange={handleChange}
                                    errors={errors}
                                    handleBlur={handleBlur}
                                />
                                <TextBox
                                    label={"Last Name"}
                                    name={"lastName"}
                                    value={values.lastName}
                                    onChange={handleChange}
                                    errors={errors}
                                    handleBlur={handleBlur}
                                />
                            </div>

                            <TextBox label="Email" name="email" value={values.email} onChange={handleChange}
                                     errors={errors} handleBlur={handleBlur}/>
                            <TextBox label="Mobile Number" name="phoneNumber" value={values.phoneNumber}
                                     onChange={handleChange} errors={errors} handleBlur={handleBlur}/>

                            <div className="flex justify-between py-5">
                                <label htmlFor="isManager">Are you a Property Manager?</label>
                                <div className="flex">
                                    <label className="mx-2">
                                        <input type="radio" name="isManager" value="true" onChange={handleChange}
                                               onBlur={handleBlur}/> Yes
                                    </label>
                                    <label className="mx-2">
                                        <input type="radio" name="isManager" value="false" onChange={handleChange}
                                               onBlur={handleBlur}/> No
                                    </label>
                                </div>
                            </div>

                            <div className="w-full py-5">
                                <label className="block text-left mb-2">Which property management software are you
                                    using?</label>
                                <div className="flex flex-wrap gap-6">
                                    {propertyManagementOptions.map((option) => (
                                        <label key={option.value} className="flex items-center">
                                            <input
                                                className="mr-2"
                                                type="radio"
                                                name="propertyManagementSoftware"
                                                value={option.value}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                checked={values.propertyManagementSoftware === option.value}
                                            />
                                            {option.label}
                                        </label>
                                    ))}
                                </div>
                                {touched.propertyManagementSoftware && errors.propertyManagementSoftware && (
                                    <p className="text-[red] mt-1">{errors.propertyManagementSoftware}</p>
                                )}
                            </div>

                            {/* Radio Buttons For isManager state */}

                            <div className="w-full mobile:w-full">
                                <TextBox
                                    label={"Company Name"}
                                    name={"companyName"}
                                    type={"text"}
                                    value={values.companyName}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    errors={errors}

                                />
                            </div>


                            <div className="flex items-center gap-x-7 mobile:flex-col">
                                <TextBox
                                    label={"Address Line 1"}
                                    name={"address.line1"}
                                    type={"text"}
                                    value={values.address.line1}
                                    onChange={handleChange}
                                    errors={errors}
                                    getIn={getIn}
                                    handleBlur={handleBlur}
                                />

                                <TextBox
                                    label={"Address Line 2"}
                                    name={"address.line2"}
                                    type={"text"}
                                    value={values.address.line2}
                                    onChange={handleChange}
                                    errors={errors}
                                    getIn={getIn}
                                    handleBlur={handleBlur}
                                />
                            </div>
                            <div className="flex items-center gap-x-7 mobile:flex-col">
                                <TextBox
                                    label={"City"}
                                    name={"address.city"}
                                    type={"text"}
                                    onChange={handleChange}
                                    value={values.address.city}
                                    errors={errors}
                                    getIn={getIn}
                                    handleBlur={handleBlur}
                                />
                                <TextBox
                                    label={"State"}
                                    name={"address.state"}
                                    type={"text"}
                                    value={values.address.state}
                                    onChange={handleChange}
                                    errors={errors}
                                    getIn={getIn}
                                    handleBlur={handleBlur}
                                />
                                <TextBox
                                    label={"Zip Code"}
                                    name={"address.zipCode"}
                                    type={"text"}
                                    value={values.address.zipCode}
                                    onChange={handleChange}
                                    errors={errors}
                                    getIn={getIn}
                                    handleBlur={handleBlur}
                                />
                            </div>
                            <div className="flex flex-col items-center w-full py-5">
                                <label className="mb-2 text-center font-bold">
                                    Please select whether you are self-signing up so we can schedule an Onboarding call
                                    to assist you

                                </label>
                                <div className="flex justify-center">
                                    <div className="mx-5">
                                        <input
                                            className="mx-2"
                                            type="radio"
                                            id="selfOnboardYes"
                                            name="selfOnboarding"
                                            value="true"
                                            onChange={handleChange}
                                            errors={errors}
                                            onBlur={handleBlur}
                                            checked={values.selfOnboarding === "true"}
                                            required
                                        />
                                        <label htmlFor="selfOnboardYes">Yes</label>
                                    </div>
                                    <div className="mx-5">
                                        <input
                                            className="mx-2"
                                            type="radio"
                                            id="selfOnboardNo"
                                            name="selfOnboarding"
                                            value="false"
                                            onChange={handleChange}
                                            errors={errors}
                                            onBlur={handleBlur}
                                            checked={values.selfOnboarding === "false"}
                                            required
                                        />
                                        <label htmlFor="selfOnboardNo">No</label>
                                    </div>
                                </div>
                            </div>
                            <TextBox
                                label={"Password"}
                                name={"password"}
                                type={"password"}
                                value={values.password}
                                onChange={handleChange}
                                errors={errors}
                                handleBlur={handleBlur}
                            />
                            {errors.password && touched.password ? (
                                <p className="text-[red]">{errors.password}</p>
                            ) : null}
                            {/* <ErrorMessage name="email" /> */}

                            <TextBox
                                label={"Confirm Password"}
                                name={"confirmPassword"}
                                type={"password"}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                            />

                            {values.password !== confirmPassword && (
                                <p className="text-[red]">Password does not match</p>
                            )}

                            {/* This is Captcha Code */}

                            <ReCAPTCHA sitekey="6LfjWG8pAAAAAPaJUlEtOre0XeDd4K6I43dsBGTq" onChange={onCaptchaChange} className="my-4" />

                            <button
                                onClick={() => handleSignUp()}
                                className=" bg-primary w-full h-[50px] rounded-[40px] mt-10"
                            >
                                <p className="font-bold text-white text-base2">
                                    {loading ? (
                                        <CircularProgress
                                            color="inherit"
                                            className="mr-2"
                                            size={"20px"}
                                        />
                                    ) : (
                                        "Sign Up"
                                    )}
                                </p>
                            </button>
                            {/* </form> */}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default SignupScreen;
